<script lang="ts" setup>
import { ref, computed, watch } from "vue";
import router from "../../router";
import { useAuthStore } from "../../store/authStore";
import {
  decodeCustomerJwtPayload,
  isCompanyAdminUserType,
  isCompanyAdminFromToken,
} from "../../utils/authRole";
import { useCompanyKyc, kycStatusFlag } from "../../composables/useCompanyKyc";

const store = useAuthStore();
const menuOpen = ref(false);

const fetchedPanda = ref(localStorage.getItem("_panda"));

const registerSession = (
  data: { user: Record<string, unknown> },
  token: string,
) => {
  store.setAuthStatus(true);
  store.setToken(token);
  store.setUserData(data.user as any);
};

/** Store may lag behind token; check both. */
const isCompanyAdmin = computed(() => {
  if (isCompanyAdminUserType(store.getUserData?.user_type)) return true;
  return isCompanyAdminFromToken(localStorage.getItem("_panda"));
});

const logwat = () => {
  store.setAuthStatus(false);
  store.$reset();
  localStorage.removeItem("_panda");
  menuOpen.value = false;
  router.push("/login");
};

const showFirstName = () => {
  const name = store.getUserData?.user_name || "";
  return name.split(" ")[0];
};

const showHotline = () => import.meta.env.VITE_HOTLINE;

// Business accounts that still owe documents get a prompt beside the menu,
// on every page, until they have uploaded them. Personal and exempt accounts
// never see it, and neither do members: verification is for admins.
const kyc = useCompanyKyc();
const kycPrompt = computed(() =>
  kyc.needsAction.value ? kycStatusFlag(kyc.status.value) : null,
);
watch(
  () => store.isAuthenticated,
  (authed) => {
    if (authed) void kyc.load();
  },
  { immediate: true },
);

const navigate = (path: string) => {
  menuOpen.value = false;
  router.push(path);
};

if (fetchedPanda.value) {
  const decoded = decodeCustomerJwtPayload(fetchedPanda.value);
  if (!decoded) {
    logwat();
  } else {
    registerSession(decoded, fetchedPanda.value);
    store.setAuthStatus(true);
  }
}
</script>

<template>
  <header class="site-header">
    <div class="header-inner">
      <!-- Logo -->
      <router-link to="/request" class="header-logo">
        <img src="/xobo.png" alt="XOBO logo" class="logo-image" />
      </router-link>

      <!-- Right side: unauthenticated -->
      <div v-if="!store.isAuthenticated" class="header-right">
        <router-link to="/login" class="header-nav-link">Sign in</router-link>
      </div>

      <!-- Right side: authenticated -->
      <div v-else class="header-right">
        <span class="header-hotline" v-if="showHotline()">
          <span class="hotline-label">Helpline</span>
          <span class="hotline-number">{{ showHotline() }}</span>
        </span>

        <router-link
          v-if="kycPrompt && isCompanyAdmin"
          to="/verify"
          class="header-kyc"
          :class="`header-kyc--${kycPrompt.tone}`"
          :title="kycPrompt.label"
          :aria-label="kycPrompt.label"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
          <span class="header-kyc-text">{{ kycPrompt.label }}</span>
        </router-link>

        <div class="header-user">
          <span class="user-greeting"
            >Hi, <strong>{{ showFirstName() }}</strong></span
          >
          <span class="user-company">{{
            store.getUserData?.company_name
          }}</span>
        </div>

        <div class="menu-wrap">
          <!-- Menu trigger -->
          <button
            class="menu-trigger"
            @click="menuOpen = !menuOpen"
            :class="{ active: menuOpen }"
          >
            <span>Menu</span>
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path
                d="M2 4l4 4 4-4"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>

          <!-- Dropdown menu -->
          <transition name="menu-slide">
            <div v-if="menuOpen" class="nav-dropdown">
              <div class="nav-dropdown-inner">
                <div class="nav-section">
                  <div class="nav-section-label">Orders</div>
                  <button class="nav-item" @click="navigate('/request')">
                    New Order
                  </button>
                </div>

                <div class="nav-divider"></div>

                <div class="nav-section">
                  <div class="nav-section-label">Transactions</div>
                  <button
                    v-if="isCompanyAdmin"
                    class="nav-item"
                    @click="navigate('/transactions/statement')"
                  >
                    Statement
                  </button>
                  <button
                    class="nav-item"
                    @click="navigate('/transactions/orders')"
                  >
                    Order History
                  </button>
                  <button
                    v-if="isCompanyAdmin"
                    class="nav-item"
                    @click="navigate('/transactions/payments')"
                  >
                    Payments
                  </button>
                </div>

                <div class="nav-divider"></div>

                <div v-if="isCompanyAdmin" class="nav-section">
                  <div class="nav-section-label">Company</div>
                  <button
                    v-if="isCompanyAdmin"
                    class="nav-item"
                    @click="navigate('/account/team')"
                  >
                    Users
                  </button>
                  <button
                    v-if="isCompanyAdmin"
                    class="nav-item"
                    @click="navigate('/account/business')"
                  >
                    Business Profile
                  </button>
                  <button
                    v-if="kyc.isBusiness.value"
                    class="nav-item"
                    @click="navigate('/account/verification')"
                  >
                    Verification
                  </button>
                </div>

                <div v-if="isCompanyAdmin" class="nav-divider"></div>

                <div class="nav-section">
                  <div class="nav-section-label">Account</div>
                  <button class="nav-item" @click="navigate('/account/profile')">
                    My Profile
                  </button>
                  <button
                    class="nav-item"
                    @click="navigate('/account/password')"
                  >
                    Change Password
                  </button>
                  <button
                    v-if="isCompanyAdmin"
                    class="nav-item"
                    @click="navigate('/account/settings')"
                  >
                    Settings
                  </button>
                </div>

                <div class="nav-divider"></div>

                <button class="nav-item nav-item--logout" @click="logwat">
                  Logout
                </button>
              </div>
            </div>
          </transition>
        </div>
      </div>
    </div>

    <!-- Backdrop -->
    <div v-if="menuOpen" class="nav-backdrop" @click="menuOpen = false"></div>
  </header>
</template>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&family=DM+Serif+Display&display=swap");

.site-header {
  position: relative;
  z-index: 100;
  background: white;
  border-bottom: 4px solid #eb1f28;
  font-family: "DM Sans", sans-serif;
}

.header-inner {
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 24px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

/* Logo */
.header-logo {
  display: flex;
  align-items: center;
  gap: 10px;
  text-decoration: none;
}

.logo-image {
  height: 32px;
  width: auto;
  object-fit: contain;
  display: block;
  flex-shrink: 0;
}

/* Right side */
.header-right {
  display: flex;
  align-items: center;
  gap: 20px;
}

.header-nav-link {
  font-size: 0.87rem;
  color: #12284a;
  text-decoration: none;
  font-weight: 500;
}

/* Hotline */
.header-hotline {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  line-height: 1.2;
}

.hotline-label {
  font-size: 0.68rem;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.hotline-number {
  font-size: 0.82rem;
  font-weight: 500;
  color: #374151;
}

/* Verification prompt */
.header-kyc {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 20px;
  border: 1px solid transparent;
  font-size: 0.78rem;
  font-weight: 600;
  text-decoration: none;
  white-space: nowrap;
  transition: filter 0.15s;
}

.header-kyc:hover {
  filter: brightness(0.97);
}

.header-kyc--warn {
  color: #b45309;
  background: #fffbeb;
  border-color: #fde68a;
}

.header-kyc--bad {
  color: #b91c1c;
  background: #fef2f2;
  border-color: #fecaca;
}

/* The header has no room for the words on a phone; the icon stays. */
@media (max-width: 720px) {
  .header-kyc {
    padding: 6px 8px;
  }

  .header-kyc-text {
    display: none;
  }
}

/* User info */
.header-user {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  line-height: 1.25;
}

.user-greeting {
  font-size: 0.82rem;
  color: #374151;
}

.user-greeting strong {
  font-weight: 600;
  color: #0f1c2e;
}

.user-company {
  font-size: 0.72rem;
  color: #9ca3af;
}

/* Menu trigger */
.menu-trigger {
  display: flex;
  align-items: center;
  gap: 6px;
  background: #12284a;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 7px 14px;
  font-family: "DM Sans", sans-serif;
  font-size: 0.82rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s;
}

.menu-trigger:hover {
  background: #1a3a6a;
}

.menu-trigger svg {
  transition: transform 0.2s;
}

.menu-trigger.active svg {
  transform: rotate(180deg);
}

.menu-wrap {
  position: relative;
}

/* Dropdown */
.nav-dropdown {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  width: 220px;
  background: white;
  border: 1px solid #eef0f3;
  border-radius: 10px;
  box-shadow:
    0 8px 24px rgba(0, 0, 0, 0.1),
    0 2px 6px rgba(0, 0, 0, 0.05);
  z-index: 200;
  overflow: hidden;
}

.nav-dropdown-inner {
  padding: 8px;
}

.nav-section {
  padding: 4px 0;
}

.nav-section-label {
  font-size: 0.68rem;
  font-weight: 600;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  padding: 4px 10px 2px;
}

.nav-item {
  display: block;
  width: 100%;
  padding: 7px 10px;
  background: none;
  border: none;
  border-radius: 6px;
  font-family: "DM Sans", sans-serif;
  font-size: 0.87rem;
  color: #374151;
  text-align: left;
  cursor: pointer;
  transition:
    background 0.1s,
    color 0.1s;
}

.nav-item:hover {
  background: #f5f7fa;
  color: #0f1c2e;
}

.nav-divider {
  height: 1px;
  background: #eef0f3;
  margin: 4px 0;
}

.nav-item--logout {
  color: #e74c3c;
  font-weight: 500;
}

.nav-item--logout:hover {
  background: #fff5f5;
  color: #c0392b;
}

/* Backdrop */
.nav-backdrop {
  position: fixed;
  inset: 0;
  z-index: 99;
}

/* Transition */
.menu-slide-enter-active,
.menu-slide-leave-active {
  transition:
    opacity 0.15s ease,
    transform 0.15s ease;
}
.menu-slide-enter-from,
.menu-slide-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

/* On a phone the header held the logo, the helpline, the user's name and
   company and the menu in one 60px row, and ran off the right of the screen
   on every page. Keep what a customer on the move needs — the logo, the
   helpline and the menu — and drop the "Hi, name / company" block. */
@media (max-width: 640px) {
  .header-inner {
    padding: 0 16px;
  }

  .header-right {
    gap: 10px;
  }

  .header-user {
    display: none;
  }

  .logo-image {
    height: 26px;
  }
}
</style>
